import { useCallback, useMemo } from 'react';
import { useUpdateHabitGoalTaskOrderMutation } from 'shared/hooks/queries/use-update-habit-goal-task-order-mutation';
import { useUpdateTaskMutation } from 'shared/hooks/queries/use-update-task-mutation';
import { Habit } from 'shared/types/habit';
import { goalInboxID, SectionBase } from 'shared/types/orderable-section';

export const useGoalHabitReorder = (sections: SectionBase<Habit>[]) => {
  const { mutate: updateGoalSorting } = useUpdateHabitGoalTaskOrderMutation();
  const { mutate: updateTask } = useUpdateTaskMutation();

  const oldSections = useMemo(() => structuredClone(sections), [sections]);

  return useCallback(
    (newSections: SectionBase<Habit>[], movedItem: Habit) => {
      const newSection = newSections.find(({ items }) =>
        items.find(({ id }) => id === movedItem.id),
      );
      const oldSection = oldSections.find(({ items }) =>
        items.find(({ id }) => id === movedItem.id),
      );

      if (!newSection || !oldSection) {
        return;
      }

      const goalsToUpdate = [
        {
          goalId: newSection.id,
          taskIds: newSection.items.map(({ id }) => id),
        },
      ];

      // if the section changed, get the new oldSection variant and add it to the goalsToUpdate list
      if (newSection.id !== oldSection.id) {
        const newOldSection = newSections.find(
          ({ id }) => id === oldSection.id,
        );

        if (!newOldSection) {
          // should not be possible, just check for type-safety
          return;
        }

        goalsToUpdate.push({
          goalId: newOldSection.id,
          taskIds: newOldSection.items.map(({ id }) => id),
        });

        updateTask({
          id: movedItem.id,
          goalId: newSection.id === goalInboxID ? null : newSection.id,
        });
      }

      updateGoalSorting(goalsToUpdate);
    },
    [oldSections, updateGoalSorting, updateTask],
  );
};
